import { useState, useContext } from "react";
import { useLocation } from "@tanstack/react-router";
import { ChatContext } from "../../contexts/ChatContext";
import { useSendChatMessage } from "../../hooks/useSendChatMessage";
import UserContext from "../../contexts/UserContext";
import AuthorisationContext from "../../contexts/AuthorisationContext";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import SendIcon from "@mui/icons-material/Send";

export default function ChatInput() {
  const [input, setInput] = useState("");
  const { threadId, setThreadId, isLoading, setIsLoading, addMessage } =
    useContext(ChatContext);
  const { user } = useContext(UserContext);
  const { authorisation } = useContext(AuthorisationContext);
  const { mutate } = useSendChatMessage();
  const location = useLocation();

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    addMessage("user", trimmed);
    setInput("");
    setIsLoading(true);

    // Only send user context on the first message of a thread
    const userContext = !threadId
      ? {
          name: user.name,
          role: user.userType,
          account: user.custName,
          access: authorisation.roles,
        }
      : undefined;

    mutate(
      {
        message: trimmed,
        threadId,
        userContext,
        currentPage: location.pathname,
      },
      {
        onSuccess: (res) => {
          const data = res.data;
          addMessage("assistant", data.response);
          if (data.threadId) {
            setThreadId(data.threadId);
          }
          setIsLoading(false);
        },
        onError: () => {
          addMessage(
            "assistant",
            "Sorry, something went wrong. Please try again.",
          );
          setIsLoading(false);
        },
      },
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        p: 2,
        borderTop: 1,
        borderColor: "divider",
        gap: 1,
      }}
    >
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        placeholder="Type your question..."
        multiline
        maxRows={4}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isLoading}
      />
      <IconButton
        color="primary"
        onClick={handleSend}
        disabled={!input.trim() || isLoading}
        aria-label="Send message"
      >
        <SendIcon />
      </IconButton>
    </Box>
  );
}
